import React, { useRef, useEffect } from "react"
import { OutlinedInput, InputAdornment, IconButton, useTheme, Stack, backdropClasses } from "@mui/material"
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded"
import ClearIcon from "@mui/icons-material/Clear"
import { scrollbarMixin } from "src/providers/AppProviders"

interface KmUserInputProps {
  query: string
  setQuery: (query: string) => void
  loading: boolean
  placeholder: string
  initialPlaceholder: string
  modelSelected: boolean
  noQuestionsYet: boolean
  handleSubmit: (e: any) => void
  clear?: () => void
  skipEndAdornment?: boolean
  minRows?: number
  onFocusChange?: (isFocused: boolean) => void
}

export const KmUserInput: React.FC<KmUserInputProps> = ({
  query,
  setQuery,
  loading,
  placeholder,
  initialPlaceholder,
  modelSelected,
  noQuestionsYet,
  handleSubmit,
  clear,
  skipEndAdornment = false,
  minRows = 1,
  onFocusChange
}) => {
  const { palette } = useTheme()
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!loading) {
      inputRef.current?.focus()
    }
  }, [loading])

  return (
    <form onSubmit={handleSubmit}>
      <OutlinedInput
        data-testid="km-user-input"
        fullWidth
        disabled={loading}
        inputRef={inputRef}
        autoFocus={false}
        multiline
        minRows={minRows}
        maxRows={6}
        id="userInput"
        name="userInput"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => onFocusChange?.(true)}
        onBlur={() => onFocusChange?.(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !!query) {
            e.preventDefault()
            handleSubmit(e)
          }
        }}
        placeholder={loading ? "Waiting for response..." : noQuestionsYet ? initialPlaceholder : placeholder}
        sx={{
          fontSize: { xs: "16px", md: "18px" },
          lineHeight: "23px",
          letterSpacing: "0",
          py: { xs: 0, md: 2 },          
          px: { xs: 2, md: 0 },
          backgroundColor: "transparent",
          border: "none",
          "&.MuiOutlinedInput-root": {
            backgroundColor: "transparent",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent"
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent"
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent"
            }
          }
        }}
        inputProps={{
          sx: {
            ...scrollbarMixin("small", "#E5E5E5")
          }
        }}
        endAdornment={
          !skipEndAdornment && (
            <InputAdornment position="end">
              <Stack direction="row" spacing={{ xs: 0.5, md: 1 }}>
                {
                  <IconButton
                    data-testid="km-send-message-button"
                    disabled={loading || !query || !modelSelected}
                    onClick={handleSubmit}
                    title="Send message"
                    sx={{
                      width: "35px",
                      height: "35px",
                      color: palette.text.primary,
                      background: palette.primary.main,
                      "&.Mui-disabled": {
                        backgroundColor: "#E2E2E2",
                        color: "#BABABA"
                      },
                      "&:hover": {
                        backgroundColor: (palette.primary as any)[400]
                      }
                    }}
                  >
                    <ArrowForwardRoundedIcon />
                  </IconButton>
                }
                {clear && (
                  <IconButton
                    data-testid="km-clear-messages-button"
                    disabled={loading || noQuestionsYet}
                    onClick={clear}
                    title="Clear context"
                    sx={{
                      width: "35px",
                      height: "35px",
                      "&.Mui-disabled": {
                        backgroundColor: "#E2E2E2",
                        color: "#BABABA"
                      }
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                )}
              </Stack>
            </InputAdornment>
          )
        }
      />
    </form>
  )
}
